// src/utils/videoUpload.js
//
// Pass 72 — the canonical student video SUBMISSION contract on the ONE `/videos` document (no new collection).
// One submission attempt = one stable videoId = one Storage path = one video doc that survives retry/failure and
// enters exactly one assignment path.
//
//   • createVideoUploadShell  — create the shell ONCE at a caller-preallocated stable id, tenant stamped at birth.
//   • markVideoUploadFailed   — persist a DURABLE failed state (was local-UI-only), so a failed upload is never
//                               claimable and can be retried on the SAME id.
//   • finalizeVideoUpload     — the ATOMIC finalization: verify ownership + pending, then in ONE transaction set
//                               the URL + uploadStatus:'complete' + details (and, for a specific coach, the
//                               assignment) — so a "specific coach" video never transiently enters the open claim
//                               pool, and an "any coach" video becomes claimable only at finalize. Idempotent.

import { runTransaction, doc, serverTimestamp } from 'firebase/firestore';

const str = (v) => (v == null ? '' : String(v).trim());
const fail = (code, msg) => { const e = new Error(msg); e.code = code; return e; };
const VIDEOS = 'videos';
const MAX_ERR = 300;

export const VIDEO_UPLOAD_DENIED = 'You do not have permission to modify this video.';
export const VIDEO_UPLOAD_NOT_FOUND = 'This video could not be found.';
export const VIDEO_UPLOAD_ALREADY_REVIEWED = 'This video has already been reviewed and cannot be changed.';
export const VIDEO_UPLOAD_ASSIGN_CONFLICT = 'This video was already finalized with a different coach.';
export const VIDEO_UPLOAD_ORG_REQUIRED = 'Select an organisation before uploading for coach review.';
export const VIDEO_UPLOAD_ALREADY_FINAL = 'This video has already finished uploading.';
export const VIDEO_UPLOAD_ASSIGNED = 'This video is already assigned to a coach and cannot be re-uploaded.';

// A sanitized, bounded upload-error category — never a raw stack/secret.
function sanitizeUploadError(err) {
  const code = str(err && err.code);
  const msg = str(err && (err.message || err)) || 'Upload failed';
  return (code ? `${code}: ${msg}` : msg).slice(0, MAX_ERR);
}

const isOwner = (v, uid) => str(v.userId) === uid || str(v.studentId) === uid;

// createVideoUploadShell(db, { videoId, actorUserId, organizationId, sport, videoName })
// The caller preallocates videoId ONCE (doc(collection(db,'videos')).id) and reuses it for Firestore + Storage +
// retry + finalization. Handles BOTH the first attempt (create) and a retry (a PERSISTED failed → uploading
// transition on the same id). Pass 72A:
//   • §5 — a modern REAL-coach shell MUST carry a tenant (organizationId) from its first write, so a tenantless
//     real-coach video can never exist.
//   • §9 — on retry the persisted uploadStatus really becomes 'uploading' (was a no-op that left it 'failed'),
//     preserving id/owner/org/createdAt/assignment/status; §10 fails closed on reviewed / assigned / already-final
//     / another owner.
// Pass 85.1A.1 (§13/§14) — `participantId` is the durable MODERN coaching identity. A canonical participant-linked
// student stamps it (with organizationId) at BIRTH, so the video is visible in their modern `videos(org,participantId)`
// history and rules can prove the participant self relationship. `userId`/`studentId` remain legacy/audit metadata. A
// legacy account (no participant profile) passes no participantId and keeps the existing tenantless-safe contract.
export async function createVideoUploadShell(db, { videoId = '', actorUserId = '', organizationId = '', participantId = '', sport = '', videoName = '' } = {}) {
  const id = str(videoId);
  const uid = str(actorUserId);
  const org = str(organizationId);
  const pid = str(participantId);
  if (!id) throw fail('VIDEO_ID_REQUIRED', 'A video id is required.');
  if (!uid) throw fail('VIDEO_ACTOR_REQUIRED', 'You must be signed in to upload.');
  if (!org) throw fail('VIDEO_ORG_REQUIRED', VIDEO_UPLOAD_ORG_REQUIRED);

  let created = false; let restarted = false;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, VIDEOS, id);
    const snap = await tx.get(ref);
    if (!snap.exists || !snap.exists()) {
      tx.set(ref, {
        userId: uid, studentId: uid, coachType: 'real', status: 'pending', uploadStatus: 'uploading',
        assigned: false, assignedTo: null, videoName: str(videoName) || 'Untitled Video', sport: str(sport),
        organizationId: org, ...(pid ? { participantId: pid } : {}), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      created = true;
      return;
    }
    // RETRY on the SAME id → validate, then a durable failed → uploading transition (no new doc). The modern
    // participant identity (participantId/org) is NEVER changed here — only the upload-lifecycle fields transition.
    const v = { id, ...snap.data() };
    if (!isOwner(v, uid)) throw fail('VIDEO_DENIED', VIDEO_UPLOAD_DENIED);
    if (v.status === 'reviewed') throw fail('VIDEO_ALREADY_REVIEWED', VIDEO_UPLOAD_ALREADY_REVIEWED);
    if (v.assigned === true || str(v.assignedInstructorId) || str(v.assignedTo)) throw fail('VIDEO_ASSIGNED', VIDEO_UPLOAD_ASSIGNED);
    if (v.uploadStatus === 'complete') throw fail('VIDEO_ALREADY_FINAL', VIDEO_UPLOAD_ALREADY_FINAL);
    if (v.uploadStatus === 'uploading') return; // already in-flight → idempotent no-op
    // failed (or any non-terminal, non-complete) → restart, clearing the failure markers.
    tx.set(ref, { uploadStatus: 'uploading', uploadError: '', uploadFailedAt: null, updatedAt: serverTimestamp() }, { merge: true });
    restarted = true;
  });
  return { videoId: id, created, restarted };
}

// markVideoUploadFailed(db, { videoId, actorUserId, uploadError })
// Durable failure: a failed upload is excluded from every queue and can be retried (failed → uploading → complete)
// on the SAME id. Never overwrites a completed/reviewed video.
export async function markVideoUploadFailed(db, { videoId = '', actorUserId = '', uploadError = null } = {}) {
  const id = str(videoId);
  const uid = str(actorUserId);
  if (!id || !uid) throw fail('VIDEO_ID_REQUIRED', 'A video id and signed-in user are required.');
  await runTransaction(db, async (tx) => {
    const ref = doc(db, VIDEOS, id);
    const snap = await tx.get(ref);
    if (!snap.exists || !snap.exists()) throw fail('VIDEO_NOT_FOUND', VIDEO_UPLOAD_NOT_FOUND);
    const v = { id, ...snap.data() };
    if (!isOwner(v, uid)) throw fail('VIDEO_DENIED', VIDEO_UPLOAD_DENIED);
    if (v.status === 'reviewed' || v.uploadStatus === 'complete') return; // never fail a finished upload
    tx.set(ref, {
      uploadStatus: 'failed',
      uploadError: sanitizeUploadError(uploadError),
      uploadFailedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
  return { videoId: id, failed: true };
}

// finalizeVideoUpload(db, { videoId, actorUserId, videoURL, videoName, sport, studentNotes, focusTags, difficulty,
//                           organizationId, coachAuthUserId })
// ATOMIC: sets URL + uploadStatus:'complete' + details, and (specific coach only) the assignment, in ONE write.
// Idempotent — a second identical finalize is a no-op success; a finalize that would change an already-set
// specific coach fails visibly (no silent reassignment).
export async function finalizeVideoUpload(db, {
  videoId = '', actorUserId = '', videoURL = '', videoName = '', sport = '',
  studentNotes = '', focusTags = null, difficulty = '', organizationId = '', coachAuthUserId = '',
} = {}) {
  const id = str(videoId);
  const uid = str(actorUserId);
  const coach = str(coachAuthUserId);
  if (!id || !uid) throw fail('VIDEO_ID_REQUIRED', 'A video id and signed-in user are required.');
  if (!str(videoURL)) throw fail('VIDEO_URL_REQUIRED', 'The uploaded video URL is required to finalize.');

  let alreadyFinal = false;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, VIDEOS, id);
    const snap = await tx.get(ref);
    if (!snap.exists || !snap.exists()) throw fail('VIDEO_NOT_FOUND', VIDEO_UPLOAD_NOT_FOUND);
    const v = { id, ...snap.data() };
    if (!isOwner(v, uid)) throw fail('VIDEO_DENIED', VIDEO_UPLOAD_DENIED);
    if (v.status === 'reviewed') throw fail('VIDEO_ALREADY_REVIEWED', VIDEO_UPLOAD_ALREADY_REVIEWED);

    const existingAssignee = str(v.assignedInstructorId) || str(v.assignedTo);
    // idempotency + no silent reassignment: if already complete, a matching outcome is a no-op; a different
    // specific coach is a visible conflict.
    if (v.uploadStatus === 'complete') {
      if (existingAssignee === coach) { alreadyFinal = true; return; }
      throw fail('VIDEO_ASSIGN_CONFLICT', VIDEO_UPLOAD_ASSIGN_CONFLICT);
    }
    // a coach may only be set on a still-unassigned video (never steal/rewrite an existing assignment).
    if (coach && existingAssignee && existingAssignee !== coach) throw fail('VIDEO_ASSIGN_CONFLICT', VIDEO_UPLOAD_ASSIGN_CONFLICT);

    const payload = {
      videoURL: str(videoURL),
      uploadStatus: 'complete',
      status: 'pending',
      updatedAt: serverTimestamp(),
    };
    if (str(videoName)) payload.videoName = str(videoName);
    if (str(sport)) payload.sport = str(sport);
    if (str(studentNotes)) payload.studentNotes = str(studentNotes);
    if (Array.isArray(focusTags)) payload.focusTags = focusTags;
    if (str(difficulty)) payload.difficulty = str(difficulty);
    // stamp org only if not already present (org is normally set at shell creation).
    if (str(organizationId) && !str(v.organizationId)) payload.organizationId = str(organizationId);
    if (coach) {
      // specific coach: assignment set ATOMICALLY with completion → never transiently claimable.
      payload.assignedInstructorId = coach;
      payload.assignedTo = coach;
      payload.assigned = true;
      payload.assignedAt = serverTimestamp();
    }
    tx.set(ref, payload, { merge: true });
  });
  return { videoId: id, finalized: true, alreadyFinal, assignedCoach: coach || '' };
}
